/**
 * Permission utility functions
 * Decodes JWT token to get user permissions
 */

/**
 * Decode JWT token without verification (client-side only)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null
 */
function decodeJWT(token) {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Get user permissions from JWT token
 * @returns {string[]} Array of permission strings
 */
export function getUserPermissions() {
  const token = localStorage.getItem('access_token');
  if (!token) {
    if (import.meta.env.DEV) {
      console.log('No access token found');
    }
    return [];
  }
  
  const decoded = decodeJWT(token);
  if (!decoded) {
    if (import.meta.env.DEV) {
      console.log('Failed to decode JWT token');
    }
    return [];
  }
  
  // Permissions are stored in the token payload
  const permissions = decoded.permissions || [];
  
  if (import.meta.env.DEV) {
    console.log('User permissions from token:', {
      permissions,
      isStaff: decoded.isStaff,
      role: decoded.role,
      fullPayload: decoded,
    });
  }
  
  return permissions;
}

/**
 * Check if current user is a staff user
 * @returns {boolean}
 */
export function isStaffUser() {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  
  const decoded = decodeJWT(token);
  if (!decoded) return false;
  
  // Check if user is staff from token payload
  return decoded.isStaff === true;
}

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission to check (e.g., 'engines.view', 'engines.edit')
 * @returns {boolean}
 */
export function hasPermission(permission) {
  if (!permission) return true; // No permission required = always allowed
  
  const permissions = getUserPermissions();
  const isStaff = isStaffUser();
  const role = localStorage.getItem('user_role');
  
  // Main entities (ADMIN, ENGINE, etc.) bypass permission checks
  // Only check permissions for staff users
  if (!isStaff) {
    // Check if it's a main entity role (not staff)
    if (['ADMIN', 'ENGINE', 'RESELLER', 'MERCHANT'].includes(role)) {
      return true; // Main entities have all permissions
    }
  }
  
  // For staff users or if no role found, check permissions array
  if (permissions.length === 0) {
    // If staff user has no permissions, deny access
    if (isStaff) {
      console.warn(`Staff user has no permissions. Denying access to: ${permission}`);
      return false;
    }
    // If not staff and no permissions, might be unauthenticated
    return false;
  }
  
  const hasAccess = permissions.includes(permission);
  
  // Debug logging in development
  if (import.meta.env.DEV && !hasAccess) {
    console.log(`Permission check failed: ${permission}`, {
      userPermissions: permissions,
      isStaff,
      role,
    });
  }
  
  return hasAccess;
}

/**
 * Check if user has any of the specified permissions
 * @param {string[]} permissionList - Array of permissions to check
 * @returns {boolean}
 */
export function hasAnyPermission(permissionList) {
  if (!permissionList || permissionList.length === 0) return true;
  return permissionList.some(permission => hasPermission(permission));
}

/**
 * Check if user has all of the specified permissions
 * @param {string[]} permissionList - Array of permissions to check
 * @returns {boolean}
 */
export function hasAllPermissions(permissionList) {
  if (!permissionList || permissionList.length === 0) return true;
  return permissionList.every(permission => hasPermission(permission));
}

/**
 * Get permission requirements for different actions
 */
export const PERMISSION_MAP = {
  // Engines
  'engines.view': ['engines.view'],
  'engines.create': ['engines.create'],
  'engines.edit': ['engines.edit'],
  'engines.delete': ['engines.delete'],
  
  // Resellers
  'resellers.view': ['resellers.view'],
  'resellers.create': ['resellers.create'],
  'resellers.edit': ['resellers.edit'],
  'resellers.delete': ['resellers.delete'],
  
  // Merchants
  'merchants.view': ['merchants.view'],
  'merchants.create': ['merchants.create'],
  'merchants.edit': ['merchants.edit'],
  'merchants.delete': ['merchants.delete'],
  
  // Transactions
  'transactions.view': ['transactions.view'],
  'transactions.edit': ['transactions.edit'],
  
  // Staff
  'staff.view': ['staff.view'],
  'staff.create': ['staff.create'],
  'staff.edit': ['staff.edit'],
  'staff.delete': ['staff.delete'],
  
  // Roles
  'roles.view': ['roles.view'],
  'roles.create': ['roles.create'],
  'roles.edit': ['roles.edit'],
  'roles.delete': ['roles.delete'],
};

/**
 * Check if user can access a page/section
 * @param {string} section - Section name (e.g., 'engines', 'resellers')
 * @returns {boolean}
 */
export function canAccessSection(section) {
  const viewPermission = `${section}.view`;
  return hasPermission(viewPermission);
}

/**
 * Check if user can perform an action
 * @param {string} resource - Resource name (e.g., 'engines', 'resellers')
 * @param {string} action - Action name (e.g., 'create', 'edit', 'delete')
 * @returns {boolean}
 */
export function canPerformAction(resource, action) {
  const permission = `${resource}.${action}`;
  return hasPermission(permission);
}

export default {
  getUserPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessSection,
  canPerformAction,
  PERMISSION_MAP,
};




